import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const TERMINAL = new Set(['success', 'failed']);

export interface CompileSettleOptions {
  /** Poll cadence in ms. */
  intervalMs?: number;
  /** Hard stop so a stuck queue can never poll forever. */
  timeoutMs?: number;
}

/**
 * Bounded watch on an explicitly re-queued compile.
 *
 * Realtime is the fast path, but a compile that starts and finishes between
 * two frames (or a dropped socket) can leave the viewer holding the previous
 * PDF — `artifact_path` is stable per version, so nothing else signals that
 * the same version produced new bytes. This polls the version row until it
 * reaches a terminal compile state and then fires `onSettled` exactly once,
 * which the page turns into a single forced PDF refetch.
 *
 * A newer watch supersedes the previous one, and unmounting cancels it.
 */
export function useCompileSettleWatch(
  onSettled: () => void,
  options: CompileSettleOptions = {},
) {
  const { intervalMs = 2000, timeoutMs = 90_000 } = options;
  const tokenRef = useRef(0);
  const mountedRef = useRef(true);
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Invalidate any watch still in flight.
      tokenRef.current += 1;
    };
  }, []);

  return useCallback(
    async (versionId: string) => {
      if (!versionId) return;
      const token = ++tokenRef.current;
      const deadline = Date.now() + timeoutMs;
      const alive = () => mountedRef.current && tokenRef.current === token;

      while (alive() && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        if (!alive()) return;
        const { data } = await supabase
          .from('writer_versions')
          .select('compile_status')
          .eq('id', versionId)
          .maybeSingle();
        if (!alive()) return;
        const status = (data as { compile_status?: string } | null)?.compile_status;
        if (status && TERMINAL.has(status)) {
          settledRef.current();
          return;
        }
      }
    },
    [intervalMs, timeoutMs],
  );
}

export default useCompileSettleWatch;
